/** Shared API auth for Design Studio Worker (DESIGN_STUDIO_KEY / ?KEY=). */

export function apiBase(): string {
  const fromEnv = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

export function apiKey(): string {
  return String(import.meta.env.VITE_OPENPOLOTNO_KEY || '').trim();
}

/**
 * Ensure /api URLs carry the real KEY. OpenPolotno often appends KEY= (empty)
 * via getKey(); we must replace empty/wrong values, not skip them.
 */
export function withApiKey(url: string): string {
  const key = apiKey();
  if (!key || !url) return url;

  const isApi =
    url.includes('/api/') ||
    url.startsWith('/api/') ||
    /(?:^|\/\/)[^/]+\/api\//.test(url);
  if (!isApi) return url;

  try {
    const base =
      typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid';
    const absolute = /^https?:\/\//i.test(url) ? url : new URL(url, base).toString();
    const u = new URL(absolute);
    const existing = u.searchParams.get('KEY');
    if (existing === key) {
      // Keep relative form if input was relative
      if (!/^https?:\/\//i.test(url)) {
        return `${u.pathname}${u.search}${u.hash}`;
      }
      return u.toString();
    }
    u.searchParams.set('KEY', key);
    if (!/^https?:\/\//i.test(url)) {
      return `${u.pathname}${u.search}${u.hash}`;
    }
    return u.toString();
  } catch {
    // Fallback: strip empty KEY= then append
    let next = url.replace(/([?&])KEY=(?:&|$)/, '$1').replace(/[?&]$/, '');
    next = next.replace(/([?&])KEY=[^&]*/, '$1');
    next = next.replace(/\?&/, '?').replace(/[?&]$/, '');
    const sep = next.includes('?') ? '&' : '?';
    return `${next}${sep}KEY=${encodeURIComponent(key)}`;
  }
}

let interceptorsInstalled = false;

/**
 * OpenPolotno loads catalog preview/json URLs as raw img.src / fetch without
 * a valid KEY. Patch fetch + HTMLImageElement.src so /api/* always carries KEY.
 */
export function installApiAuthInterceptors(): void {
  if (typeof window === 'undefined' || interceptorsInstalled) return;
  const key = apiKey();
  if (!key) {
    console.warn('apiAuth: VITE_OPENPOLOTNO_KEY missing — /api calls may 401');
    return;
  }
  interceptorsInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      if (typeof input === 'string') {
        return originalFetch(withApiKey(input), init);
      }
      if (input instanceof URL) {
        return originalFetch(withApiKey(input.toString()), init);
      }
      if (input instanceof Request) {
        const next = withApiKey(input.url);
        if (next !== input.url) {
          return originalFetch(new Request(next, input), init);
        }
      }
    } catch {
      /* fall through */
    }
    return originalFetch(input as RequestInfo, init);
  }) as typeof window.fetch;

  try {
    const desc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
    if (desc?.set && desc?.get) {
      Object.defineProperty(HTMLImageElement.prototype, 'src', {
        configurable: true,
        enumerable: desc.enumerable,
        get() {
          return desc.get!.call(this);
        },
        set(value: string) {
          desc.set!.call(this, withApiKey(String(value ?? '')));
        },
      });
    }
  } catch (err) {
    console.warn('apiAuth: could not patch Image.src', err);
  }
}
